import { apiClient } from '../api/apiClient';
import type {
  Question,
  CreateQuestionRequest,
  Assessment,
  CreateAssessmentRequest,
  AssessmentAttempt,
  StartAssessmentResponse,
  SubmitAnswerRequest,
  SubmitAnswerResponse,
  AssessmentAnalytics,
  PaginatedResponse,
  ApiResponse,
  QueryParams,
  ExcelImportResponse,
  AttemptsListResponse,
  AttemptDetails,
  AttemptResult,
} from '@/types/assessment';

class AssessmentService {
  private readonly baseUrl = '/assessment';

  // ===============================
  // Question Management APIs
  // ===============================

  /**
   * Create a new question
   * POST /api/assessment/questions
   */
  async createQuestion(questionData: CreateQuestionRequest): Promise<ApiResponse<{ question: Question }>> {
    return apiClient.post(`${this.baseUrl}/questions`, questionData);
  }

  /**
   * Create a question for a specific assessment
   * POST /api/assessment/assessments/{assessmentId}/questions
   */
  async createAssessmentQuestion(assessmentId: string, questionData: CreateQuestionRequest): Promise<ApiResponse<{ question: Question }>> {
    return apiClient.post(`${this.baseUrl}/assessments/${assessmentId}/questions`, questionData);
  }

  /**
   * Bulk create questions
   * POST /api/assessment/questions/bulk
   */
  async bulkCreateQuestions(questions: CreateQuestionRequest[]): Promise<ApiResponse<{ questions: Question[] }>> {
    return apiClient.post(`${this.baseUrl}/questions/bulk`, questions);
  }

  /**
   * Bulk create questions for a specific assessment
   * POST /api/assessment/assessments/{assessmentId}/questions/bulk
   */
  async bulkCreateAssessmentQuestions(assessmentId: string, questions: CreateQuestionRequest[]): Promise<ApiResponse<{ questions: Question[] }>> {
    return apiClient.post(`${this.baseUrl}/assessments/${assessmentId}/questions/bulk`, questions);
  }

  /**
   * Get all questions with pagination and filtering
   * GET /api/assessment/questions
   */
  async getQuestions(params?: QueryParams): Promise<ApiResponse<PaginatedResponse<Question>>> {
    const queryString = params ? new URLSearchParams(params as any).toString() : '';
    // if(params)
    const url = queryString ? `${this.baseUrl}/questions?${queryString}` : `${this.baseUrl}/questions`;
    return apiClient.get(url);
  }

  /**
   * Get questions for a specific assessment
   * GET /api/assessment/assessments/{assessmentId}/questions
   */
  async getAssessmentQuestions(assessmentId: string, params?: QueryParams): Promise<ApiResponse<PaginatedResponse<Question>>> {
    const queryString = params ? new URLSearchParams(params as any).toString() : '';
    const url = queryString 
      ? `${this.baseUrl}/assessments/${assessmentId}/questions?${queryString}` 
      : `${this.baseUrl}/assessments/${assessmentId}/questions`;
    return apiClient.get(url);
  }

  /**
   * Get single question by ID
   * GET /api/assessment/questions/{id}
   */
  async getQuestion(id: string): Promise<ApiResponse<{ question: Question }>> {
    return apiClient.get(`${this.baseUrl}/questions/${id}`);
  }

  /**
   * Update question
   * PUT /api/assessment/questions/{id}
   */
  async updateQuestion(id: string, questionData: CreateQuestionRequest): Promise<ApiResponse<{ question: Question }>> {
    return apiClient.put(`${this.baseUrl}/questions/${id}`, questionData);
  }

  /**
   * Delete question
   * DELETE /api/assessment/questions/{id}
   */
  async deleteQuestion(id: string): Promise<ApiResponse<{}>> {
    return apiClient.delete(`${this.baseUrl}/questions/${id}`);
  }

  // ===============================
  // Assessment Management APIs
  // ===============================

  /**
   * Create a new assessment
   * POST /api/assessment/assessments
   */
  async createAssessment(assessmentData: CreateAssessmentRequest): Promise<ApiResponse<{ assessment: Assessment }>> {
    return apiClient.post(`${this.baseUrl}/assessments`, assessmentData);
  }

  /**
   * Get all assessments with pagination and filtering
   * GET /api/assessment/assessments
   */
  async getAssessments(params?: QueryParams): Promise<ApiResponse<PaginatedResponse<Assessment>>> {
    const queryString = params ? new URLSearchParams(params as any).toString() : '';
    const url = queryString ? `${this.baseUrl}/assessments?${queryString}` : `${this.baseUrl}/assessments`;
    return apiClient.get(url);
  }

  /**
   * Get single assessment by ID
   * GET /api/assessment/assessments/{id}
   */
  async getAssessment(id: string): Promise<ApiResponse<{ assessment: Assessment }>> {
    return apiClient.get(`${this.baseUrl}/assessments/${id}`);
  }

  /**
   * Update assessment
   * PUT /api/assessment/assessments/{id}
   */
  async updateAssessment(id: string, assessmentData: CreateAssessmentRequest): Promise<ApiResponse<{ assessment: Assessment }>> {
    return apiClient.put(`${this.baseUrl}/assessments/${id}`, assessmentData);
  }

  /**
   * Delete assessment
   * DELETE /api/assessment/assessments/{id}
   */
  async deleteAssessment(id: string): Promise<ApiResponse<{}>> {
    return apiClient.delete(`${this.baseUrl}/assessments/${id}`);
  }

  // ===============================
  // Assessment Taking APIs
  // ===============================

  /**
   * Check eligibility to take an assessment
   * GET /api/assessment/assessments/{id}/eligibility
   */
  async checkEligibility(assessmentId: string): Promise<ApiResponse<{
    eligible: boolean;
    reason?: string;
    nextAvailableTime?: string;
    remainingAttempts?: number;
  }>> {
    return apiClient.get(`${this.baseUrl}/assessments/${assessmentId}/eligibility`);
  }

  /**
   * Start an assessment
   * POST /api/assessment/assessments/{id}/start
   */
  async startAssessment(assessmentId: string): Promise<ApiResponse<StartAssessmentResponse>> {
    return apiClient.post(`${this.baseUrl}/assessments/${assessmentId}/start`);
  }

  /**
   * Submit an answer for a question
   * POST /api/assessment/attempts/{attemptId}/answer
   */
  async submitAnswer(attemptId: string, answerData: SubmitAnswerRequest): Promise<ApiResponse<SubmitAnswerResponse>> {
    return apiClient.post(`${this.baseUrl}/attempts/${attemptId}/answer`, answerData);
  }

  /**
   * Complete an assessment
   * POST /api/assessment/attempts/{attemptId}/complete
   */
  async completeAssessment(attemptId: string): Promise<ApiResponse<{ attempt: AssessmentAttempt }>> {
    return apiClient.post(`${this.baseUrl}/attempts/${attemptId}/complete`);
  }

  /**
   * Get assessment attempt details
   * GET /api/assessment/attempts/{attemptId}
   */
  async getAttempt(attemptId: string): Promise<ApiResponse<{ attempt: AttemptDetails }>> {
    return apiClient.get(`${this.baseUrl}/attempts/${attemptId}`);
  }

  /**
   * Get user's assessment history
   * GET /api/assessment/my-attempts
   */
  async getMyAttempts(params?: QueryParams): Promise<ApiResponse<AttemptsListResponse>> {
    const queryString = params ? new URLSearchParams(params as any).toString() : '';
    const url = queryString ? `${this.baseUrl}/my-attempts?${queryString}` : `${this.baseUrl}/my-attempts`;
    return apiClient.get(url);
  }

  /**
   * Get assessment attempt result
   * GET /api/assessment/attempts/{attemptId}/result
   */
  async getAttemptResult(attemptId: string): Promise<ApiResponse<AttemptResult>> {
    return apiClient.get(`${this.baseUrl}/attempts/${attemptId}/result`);
  }

  // ===============================
  // Analytics APIs
  // ===============================

  /**
   * Get assessment analytics
   * GET /api/assessment/assessments/{id}/analytics
   */
  async getAssessmentAnalytics(assessmentId: string): Promise<ApiResponse<{ analytics: AssessmentAnalytics }>> {
    return apiClient.get(`${this.baseUrl}/assessments/${assessmentId}/analytics`);
  }

  /**
   * Get user's personal analytics
   */
  async getMyAnalytics(): Promise<ApiResponse<any>> {
    return apiClient.get(`${this.baseUrl}/my-analytics`);
  }

  /**
   * Get system-wide analytics (admin only)
   */
  async getSystemAnalytics(): Promise<ApiResponse<any>> {
    return apiClient.get(`${this.baseUrl}/system-analytics`);
  }

  // ===============================
  // Excel Import/Export APIs
  // ===============================

  /**
   * Import questions from Excel file
   * POST /api/assessment/questions/import-excel
   */
  async importQuestionsFromExcel(file: File): Promise<ApiResponse<ExcelImportResponse>> {
    return apiClient.upload(`${this.baseUrl}/questions/import-excel`, file);
  }

  /**
   * Download sample Excel template
   * GET /api/assessment/questions/sample-excel
   */
  async downloadSampleExcel(): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/questions/sample-excel`, {
      responseType: 'blob',
      showLoader: true,
    });
    return response;
  }

  /**
   * Export questions to Excel
   */
  async exportQuestionsToExcel(params?: QueryParams): Promise<Blob> {
    const queryString = params ? new URLSearchParams(params as any).toString() : '';
    const url = queryString ? `${this.baseUrl}/questions/export-excel?${queryString}` : `${this.baseUrl}/questions/export-excel`;
    
    const response = await apiClient.get(url, {
      responseType: 'blob',
      showLoader: true,
    });
    return response;
  }

  /**
   * Export assessment results to Excel
   */
  async exportAssessmentResults(assessmentId: string): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/assessments/${assessmentId}/export-results`, {
      responseType: 'blob',
      showLoader: true,
    });
    return response;
  }

  // ===============================
  // Utility Methods
  // ===============================

  /**
   * Download file blob with specified filename
   */
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get categories for dropdowns (deprecated - use categoryService)
   * @deprecated Use categoryService.getActiveCategories() instead
   */
  getCategories(): string[] {
    console.warn('assessmentService.getCategories() is deprecated. Use categoryService.getActiveCategories() instead.');
    return ['Technical Skills', 'Soft Skills', 'Industry Knowledge', 'Compliance'];
  }

  /**
   * Get difficulty levels for dropdowns (deprecated - use difficultyService)
   * @deprecated Use difficultyService.getDifficultyLevels() instead
   */
  getDifficultyLevels(): string[] {
    console.warn('assessmentService.getDifficultyLevels() is deprecated. Use difficultyService.getDifficultyLevels() instead.');
    return ['Beginner', 'Intermediate', 'Advanced'];
  }

  /**
   * Get answer options (deprecated - use difficultyService)
   * @deprecated Use difficultyService.getAnswerOptionsLocal() instead
   */
  getAnswerOptions(): string[] {
    console.warn('assessmentService.getAnswerOptions() is deprecated. Use difficultyService.getAnswerOptionsLocal() instead.');
    return ['A', 'B', 'C', 'D'];
  }

  /**
   * Format time remaining (seconds to minutes:seconds)
   */
  formatTimeRemaining(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  /**
   * Calculate time spent in human readable format
   */
  formatTimeSpent(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  }

  /**
   * Get status color for UI components
   */
  getStatusColor(status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'abandoned':
        return 'error';
      case 'expired':
        return 'error';
      case 'pending':
      default:
        return 'default';
    }
  }

  /**
   * Get difficulty color for UI components
   */
  getDifficultyColor(difficulty: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    switch (difficulty) {
      case 'Beginner':
        return 'success';
      case 'Intermediate':
        return 'warning';
      case 'Advanced':
        return 'error';
      default:
        return 'default';
    }
  }
}

export const assessmentService = new AssessmentService();